import React, { useMemo, useRef, useState } from 'react';

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(filename = '') {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

function isImageFile(filename = '') {
  const ext = getFileExtension(filename);
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
}

function getFileTypeLabel(filename = '') {
  const ext = getFileExtension(filename);

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'Bild';
  if (['pdf'].includes(ext)) return 'PDF';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'Dokument';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'Tabelle';
  if (['ppt', 'pptx', 'odp'].includes(ext)) return 'Präsentation';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'Archiv';
  if (['txt', 'md'].includes(ext)) return 'Text';
  return ext ? ext.toUpperCase() : 'Datei';
}

function getFileIcon(filename = '') {
  const ext = getFileExtension(filename);

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return '🖼️';
  if (['pdf'].includes(ext)) return '📕';
  if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) return '📄';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return '📊';
  if (['ppt', 'pptx', 'odp'].includes(ext)) return '📽️';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return '🗜️';
  if (['txt', 'md'].includes(ext)) return '📝';
  return '📎';
}

function buildFileUrl(attachment) {
  const baseUrl = window.location.origin;

  if (attachment?.file_path) {
    if (attachment.file_path.startsWith('http://') || attachment.file_path.startsWith('https://')) {
      return attachment.file_path;
    }

    if (attachment.file_path.startsWith('/')) {
      return `${baseUrl}${attachment.file_path}`;
    }

    return `${baseUrl}/${attachment.file_path}`;
  }

  if (attachment?.filename) {
    if (attachment.filename.startsWith('/')) {
      return `${baseUrl}${attachment.filename}`;
    }

    return `${baseUrl}/uploads/${attachment.filename}`;
  }

  return '#';
}

export default function TaskAttachments({
  taskId,
  token,
  attachments = [],
  onRefresh,
  canDelete = true,
}) {
  const fileInputRef = useRef(null);

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [error, setError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);

  const sortedAttachments = useMemo(() => {
    return [...attachments].sort((a, b) => {
      const aDate = new Date(a.created_at || a.uploaded_at || 0).getTime();
      const bDate = new Date(b.created_at || b.uploaded_at || 0).getTime();
      return bDate - aDate;
    });
  }, [attachments]);

  const handleFiles = async (files) => {
    const validFiles = Array.from(files || []);
    if (!validFiles.length) return;

    setError('');
    setUploading(true);
    setUploadQueue(validFiles.map((file) => ({ name: file.name, status: 'uploading' })));

    try {
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append('attachment', file);

        const response = await fetch(`/api/tasks/${taskId}/attachments`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw new Error(data?.message || `Upload fehlgeschlagen: ${file.name}`);
        }

        setUploadQueue((prev) =>
          prev.map((item) =>
            item.name === file.name ? { ...item, status: 'done' } : item
          )
        );
      }

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      setError(err.message || 'Upload fehlgeschlagen.');
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.status === 'uploading' ? { ...item, status: 'error' } : item
        )
      );
    } finally {
      setUploading(false);
      setTimeout(() => {
        setUploadQueue([]);
      }, 1200);
    }
  };

  const handleDelete = async (attachmentId) => {
    const confirmed = window.confirm('Anhang wirklich löschen?');
    if (!confirmed) return;

    setError('');

    try {
      const response = await fetch(`/api/tasks/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || 'Löschen fehlgeschlagen.');
      }

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      setError(err.message || 'Löschen fehlgeschlagen.');
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
    }
  };

  return (
    <>
      <div className="attachments-section">
        <div className="attachments-header">
          <div>
            <h3>Anhänge</h3>
            <p>{sortedAttachments.length} Datei{sortedAttachments.length === 1 ? '' : 'en'}</p>
          </div>

          <button
            type="button"
            className="attachments-upload-button"
            onClick={openFileDialog}
            disabled={uploading}
          >
            {uploading ? 'Wird hochgeladen...' : 'Datei hochladen'}
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="attachments-hidden-input"
          onChange={(e) => handleFiles(e.target.files)}
        />

        <div
          className={`attachments-dropzone ${dragActive ? 'is-drag-active' : ''} ${uploading ? 'is-uploading' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              openFileDialog();
            }
          }}
        >
          <div className="attachments-dropzone-icon">📁</div>
          <div className="attachments-dropzone-text">
            <strong>Dateien hier ablegen</strong>
            <span>oder klicken zum Auswählen</span>
          </div>
        </div>

        {uploadQueue.length > 0 && (
          <div className="attachments-upload-queue">
            {uploadQueue.map((file, index) => (
              <div key={`${file.name}-${index}`} className={`upload-queue-item status-${file.status}`}>
                <span className="upload-queue-name">{file.name}</span>
                <span className="upload-queue-status">
                  {file.status === 'uploading' && 'Lädt hoch...'}
                  {file.status === 'done' && 'Fertig'}
                  {file.status === 'error' && 'Fehler'}
                </span>
              </div>
            ))}
          </div>
        )}

        {error && <div className="attachments-error">{error}</div>}

        {sortedAttachments.length === 0 ? (
          <div className="attachments-empty">
            Noch keine Anhänge vorhanden.
          </div>
        ) : (
          <div className="attachments-list">
            {sortedAttachments.map((attachment) => {
              const fileName = attachment.original_name || attachment.filename || 'Datei';
              const fileUrl = buildFileUrl(attachment);
              const image = isImageFile(fileName);

              return (
                <div key={attachment.id} className="attachment-card">
                  <div className="attachment-card-left">
                    {image ? (
                      <button
                        type="button"
                        className="attachment-image-preview"
                        onClick={() => setPreviewImage(fileUrl)}
                        title="Bild vergrößern"
                      >
                        <img
                          src={fileUrl}
                          alt={fileName}
                        />
                      </button>
                    ) : (
                      <div className="attachment-file-icon">
                        {getFileIcon(fileName)}
                      </div>
                    )}
                  </div>

                  <div className="attachment-card-center">
                    <div className="attachment-file-name">
                      {fileName}
                    </div>

                    <div className="attachment-meta">
                      <span className="attachment-type">
                        {getFileTypeLabel(fileName)}
                      </span>

                      {(attachment.file_size || attachment.size) && (
                        <span className="attachment-size">
                          {formatFileSize(attachment.file_size || attachment.size)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="attachment-card-right">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="attachment-action-button"
                    >
                      Öffnen
                    </a>

                    {canDelete && (
                      <button
                        type="button"
                        className="attachment-delete-button"
                        onClick={() => handleDelete(attachment.id)}
                      >
                        Löschen
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {previewImage && (
        <div className="attachment-preview-overlay" onClick={() => setPreviewImage(null)}>
          <div
            className="attachment-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="attachment-preview-close"
              onClick={() => setPreviewImage(null)}
            >
              ×
            </button>
            <img src={previewImage} alt="Vorschau" className="attachment-preview-full" />
          </div>
        </div>
      )}
    </>
  );
}